'use client';

import * as Sentry from '@sentry/nextjs';
import { useEffect } from 'react';
import { isProductionBuild } from '../utils/isProductionBuild';
import { Color } from '../styles/Color';

/**
 * Error boundary for pages within the app.
 * Catches React rendering errors in pages and reports them to Sentry in production.
 *
 * For errors in the root layout itself, see global-error.tsx
 *
 * @see https://nextjs.org/docs/app/building-your-application/routing/error-handling
 * @see https://docs.sentry.io/platforms/javascript/guides/nextjs/manual-setup/#react-render-errors-in-app-router
 */
export default function Error({
	error,
	reset,
}: {
	error: Error & { digest?: string };
	reset: () => void;
}) {
	useEffect(() => {
		if (isProductionBuild()) {
			Sentry.captureException(error);
		}
	}, [error]);

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'center',
				alignItems: 'center',
				textAlign: 'center',
				minHeight: '100vh',
				padding: '20px',
				fontFamily: 'system-ui, -apple-system, sans-serif',
			}}
		>
			<div style={{ maxWidth: '280px' }}>
				<h1
					style={{
						color: Color.Secondary,
						marginTop: '25px',
						marginBottom: '12px',
						fontSize: '1.5rem',
						fontWeight: 600,
					}}
				>
					Something went wrong
				</h1>
				<p
					style={{
						marginBottom: '10px',
						lineHeight: '22.3px',
						letterSpacing: '0.64px',
						color: Color.OnBackground,
					}}
				>
					We apologize for the inconvenience. Please try again or
				</p>
				<button
					type="button"
					onClick={reset}
					style={{
						background: 'none',
						border: 'none',
						color: Color.Secondary,
						cursor: 'pointer',
						fontSize: '16px',
						fontWeight: 600,
						padding: '8px 0',
						textDecoration: 'none',
						display: 'inline-flex',
						alignItems: 'center',
						gap: '8px',
					}}
				>
					Return to Home
					<span style={{ fontSize: '18px' }}>→</span>
				</button>
			</div>
		</div>
	);
}
